import React, { useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';

import './OfficeStatusCards.scss';

const AVATAR_COLORS = ['#38bdf8', '#8b5cf6', '#22c55e', '#f59e0b', '#ec4899', '#64748b'];

const initialsFrom = (name) => {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const colorForName = (name) => {
  let h = 0;
  const s = name || '';
  for (let i = 0; i < s.length; i += 1) {
    h = (h + s.charCodeAt(i) * (i + 3)) % AVATAR_COLORS.length;
  }
  return AVATAR_COLORS[h];
};

const PeopleRow = ({ people, visibleMax = 5, onMoreClick }) => {
  const visible = people.slice(0, visibleMax);
  const overflow = people.length > visibleMax ? people.length - visibleMax : 0;

  const moreInner = (
    <>
      <span className="office-status-card__more-bubble">+{overflow}</span>
      <span className="office-status-card__name office-status-card__name--muted">more</span>
    </>
  );

  return (
    <div className="office-status-card__avatars" role="list">
      {visible.map((p, i) => {
        const key = p.key || `${p.name}-${i}`;
        return (
          <div key={key} className="office-status-card__cell" role="listitem">
            {p.photo ? (
              <img
                className="office-status-card__avatar office-status-card__avatar--img"
                src={p.photo}
                alt=""
              />
            ) : (
              <span
                className="office-status-card__avatar office-status-card__avatar--initials"
                style={{ background: colorForName(p.name) }}
                aria-hidden
              >
                {initialsFrom(p.name)}
              </span>
            )}
            <span className="office-status-card__name" title={p.name}>
              {p.name}
            </span>
            {p.meta ? (
              <span
                className={`office-status-card__meta${
                  p.metaTone ? ` office-status-card__meta--${p.metaTone}` : ''
                }`}
              >
                {p.meta}
              </span>
            ) : null}
          </div>
        );
      })}
      {overflow > 0 ? (
        onMoreClick ? (
          <button
            type="button"
            className="office-status-card__cell office-status-card__cell--more office-status-card__more-btn"
            onClick={onMoreClick}
            aria-label={`Show all ${people.length} people`}
          >
            {moreInner}
          </button>
        ) : (
          <div
            className="office-status-card__cell office-status-card__cell--more"
            aria-label={`${overflow} more`}
          >
            {moreInner}
          </div>
        )
      ) : null}
    </div>
  );
};

const leaveMeta = (leave) => {
  const dayType = leave?.leaveDayType || '';
  if (dayType) return dayType;
  if (leave?.isHalfDay === true) return 'Half Day';
  if (leave?.isHalfDay === false) return 'Full Day';
  return '';
};

const leaveToPerson = (leave, i) => ({
  key: leave?._id || leave?.id || leave?.userId?._id || `leave-${i}`,
  name: leave?.userId?.name || 'Unknown',
  photo: leave?.userId?.profilePicture || null,
  designation: leave?.userId?.designations?.name || leave?.userId?.designation || '',
  meta: leaveMeta(leave)
});

const WFH_DAY_TYPES = {
  'full day': { label: 'Full Day', tone: 'full' },
  'first half': { label: '1st Half', tone: 'first' },
  '1st half': { label: '1st Half', tone: 'first' },
  'second half': { label: '2nd Half', tone: 'second' },
  '2nd half': { label: '2nd Half', tone: 'second' }
};

const wfhDayType = (wfh) => {
  const raw = String(wfh?.leaveType || wfh?.leaveDayType || '').trim();
  if (!raw) return { label: '', tone: '' };
  return WFH_DAY_TYPES[raw.toLowerCase()] || { label: raw, tone: 'full' };
};

const wfhToPerson = (wfh, i) => {
  const { label, tone } = wfhDayType(wfh);
  return {
    key: wfh?._id || wfh?.id || wfh?.userId || wfh?.user?._id || `wfh-${i}`,
    name: wfh?.user?.name || 'Unknown',
    photo: wfh?.user?.profilePicture || null,
    designation: wfh?.user?.designation || '',
    meta: label,
    metaTone: tone
  };
};

const OfficeStatusCards = ({
  leaveList = [],
  workFromHomeList = [],
  isHrOrAdmin = false,
  showWfh = true,
  showTodayLeave = true
}) => {
  const [peopleModal, setPeopleModal] = useState(null);

  const todayLeave = useMemo(() => {
    const src = Array.isArray(leaveList) ? leaveList : [];
    return src.map((leave, i) => leaveToPerson(leave, i));
  }, [leaveList]);

  const wfh = useMemo(() => {
    const src = Array.isArray(workFromHomeList) ? workFromHomeList : [];
    return src.map((x, i) => wfhToPerson(x, i));
  }, [workFromHomeList]);

  const count = todayLeave.length;
  const wfhCount = wfh.length;
  const canShowLeave = showTodayLeave && !isHrOrAdmin;
  const showTabs = showWfh && canShowLeave;
  const [activeTab, setActiveTab] = useState('leave');

  const modalPeople = peopleModal?.people || [];

  const leaveBody =
    count > 0 ? (
      <PeopleRow
        people={todayLeave}
        onMoreClick={() => setPeopleModal({ title: 'Today leave', people: todayLeave })}
      />
    ) : (
      <p className="office-status-card__empty">No one is on leave today.</p>
    );

  const wfhBody =
    wfhCount > 0 ? (
      <PeopleRow
        people={wfh}
        onMoreClick={() => setPeopleModal({ title: 'Work from home', people: wfh })}
      />
    ) : (
      <p className="office-status-card__empty">No WFH scheduled for today.</p>
    );

  return (
    <div className="office-status-cards">
      {/* <article className="office-status-card">
        <h3 className="office-status-card__heading">Not in office today</h3>
        {outCount > 0 ? (
          <>
            <PeopleRow people={outOffice} />
            <p className="office-status-card__footer">
              {outCount} {outCount === 1 ? 'employee is' : 'employees are'} not in office today.
            </p>
          </>
        ) : (
          <p className="office-status-card__empty">Everyone is accounted for — or working from home.</p>
        )}
      </article> */}

      {showTabs ? (
        <article className="office-status-card office-status-card--tabs">
          <div
            className="office-status-card__tabs"
            role="tablist"
            aria-label="Today leave and work from home"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'leave'}
              className="office-status-card__tab"
              onClick={() => setActiveTab('leave')}
            >
              <span>Today leave</span>
              <span className="office-status-card__count">{count}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'wfh'}
              className="office-status-card__tab"
              onClick={() => setActiveTab('wfh')}
            >
              <span>Work from home</span>
              <span className="office-status-card__count">{wfhCount}</span>
            </button>
          </div>
          <div className="office-status-card__panel">
            {activeTab === 'leave' ? leaveBody : wfhBody}
          </div>
        </article>
      ) : null}

      {!showTabs && showWfh ? (
        <article className="office-status-card office-status-card--wfh">
          <div className="office-status-card__head">
            <h3 className="office-status-card__heading">Work from home</h3>
            <span className="office-status-card__count">{wfhCount}</span>
          </div>
          {wfhBody}
        </article>
      ) : null}

      {!showTabs && canShowLeave ? (
        <article className="office-status-card office-status-card--leave">
          <div className="office-status-card__head">
            <h3 className="office-status-card__heading">Today leave</h3>
            <span className="office-status-card__count">{count}</span>
          </div>
          {leaveBody}
        </article>
      ) : null}

      <Modal
        show={Boolean(peopleModal)}
        onHide={() => setPeopleModal(null)}
        centered
        size="md"
        contentClassName="office-status-people-modal"
      >
        <Modal.Header className="office-status-people-modal__head">
          <div>
            <div className="office-status-people-modal__title">{peopleModal?.title || ''}</div>
            <div className="office-status-people-modal__sub">
              {modalPeople.length} {modalPeople.length === 1 ? 'person' : 'people'}
            </div>
          </div>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setPeopleModal(null)}
          />
        </Modal.Header>
        <Modal.Body className="office-status-people-modal__body">
          <ul className="office-status-people-modal__list" role="list">
            {modalPeople.map((p, i) => {
              const key = p.key || `${p.name}-${i}`;
              return (
                <li key={key} className="office-status-people-modal__row" role="listitem">
                  {p.photo ? (
                    <img
                      className="office-status-people-modal__avatar office-status-people-modal__avatar--img"
                      src={p.photo}
                      alt=""
                    />
                  ) : (
                    <span
                      className="office-status-people-modal__avatar office-status-people-modal__avatar--initials"
                      style={{ background: colorForName(p.name) }}
                      aria-hidden
                    >
                      {initialsFrom(p.name)}
                    </span>
                  )}
                  <div className="office-status-people-modal__content">
                    <div className="office-status-people-modal__text">
                      <span className="office-status-people-modal__name" title={p.name}>
                        {p.name}
                      </span>
                      {p.designation ? (
                        <span
                          className="office-status-people-modal__designation"
                          title={p.designation}
                        >
                          {p.designation}
                        </span>
                      ) : null}
                    </div>
                    {p.meta ? (
                      <span
                        className={`office-status-people-modal__meta${
                          p.metaTone ? ` office-status-people-modal__meta--${p.metaTone}` : ''
                        }`}
                      >
                        {p.meta}
                      </span>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default OfficeStatusCards;
